// Generic table-export helpers used across the app whenever a list
// view needs Download as CSV / Download as PDF. Same pattern as
// AdminReports — kept here so other screens (tenant payments,
// maintenance log, vacate history, ...) don't each copy the logic.
//
// Headers shape: [{ key: "dt", label: "Date" }, ...]
// Rows shape:    array of plain objects keyed by header.key
import { escHtml } from "./escHtml";

const escCsv = (v) => {
  const s = String(v ?? "");
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};

export const downloadCsv = (rows, headers, filename) => {
  if (!rows || !rows.length) return false;
  const csv = [
    headers.map(h => escCsv(h.label)).join(","),
    ...rows.map(r => headers.map(h => escCsv(r[h.key])).join(",")),
  ].join("\n");
  // Excel needs a UTF-8 BOM to render ₹ / non-ASCII characters
  // correctly when opening the file directly. Without it, the rupee
  // symbol shows as â‚¹.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return true;
};

// Print-friendly HTML opened in a new tab via Blob URL — the user
// gets the browser's "Save as PDF" dialog, which is the lightest
// cross-platform PDF generator available without bundling a heavy
// library. Same pattern used by the agreement + settlement flows.
export const downloadPdf = (rows, headers, title, meta) => {
  if (!rows || !rows.length) return false;
  const tbody = rows
    .map(r => `<tr>${headers.map(h => `<td>${escHtml(r[h.key])}</td>`).join("")}</tr>`)
    .join("");
  const html = `<!DOCTYPE html><html lang="en"><head>
    <meta charset="UTF-8">
    <title>${escHtml(title)}</title>
    <style>
      @page { margin: 14mm; size: A4 portrait; }
      body { font-family: 'Helvetica', Arial, sans-serif; padding: 16px; margin: 0; color: #222; font-size: 12px; }
      h1 { font-size: 18px; margin: 0 0 4px; color: #111; }
      .meta { color: #666; font-size: 11px; margin-bottom: 14px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { padding: 7px 9px; border: 1px solid #ddd; text-align: left; vertical-align: top; }
      th { background: #f3f4f6; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
      tr:nth-child(even) td { background: #fafafa; }
      .actions { margin-bottom: 14px; }
      button { padding: 7px 16px; border-radius: 6px; border: 1px solid #3b82f6; background: #3b82f6; color: #fff; cursor: pointer; font-size: 12px; font-weight: 600; }
      @media print { .actions { display: none; } }
    </style>
  </head><body>
    <div class="actions"><button onclick="window.print()">Print / Save as PDF</button></div>
    <h1>${escHtml(title)}</h1>
    <div class="meta">${escHtml(meta || "")} · ${rows.length} rows · Exported ${new Date().toLocaleString()}</div>
    <table>
      <thead><tr>${headers.map(h => `<th>${escHtml(h.label)}</th>`).join("")}</tr></thead>
      <tbody>${tbody}</tbody>
    </table>
  </body></html>`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (!w) {
    // Popup blocked — fall back to a link click. Less ideal because
    // the print dialog won't auto-open, but the file still reaches
    // the user.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};
